import React from 'react';
import { Modal, Spinner, Text } from '@quality24/design-system';
import { UUIDv4 } from '@quality24/inpatient-typings';
import { v4 as uuid } from 'uuid';

import PatientCallCustomFieldsModal from '@/inpatient-patient-pwa/organisms/PatientCallCustomFieldsModal';
import PatientCallCustomizationModal from '@/inpatient-patient-pwa/organisms/PatientCallCustomizationModal';
import PatientCallSelectorModal from '@/inpatient-patient-pwa/organisms/PatientCallSelectorModal';
import OrderChargeableItemsModal from '@/inpatient-patient-pwa/organisms/OrderChargeableItemsModal';
import { formatOrderCustomFieldValues } from '@/inpatient-patient-pwa/hooks/useTriggerOrder';
import { ChargeableItemFragment } from '@/inpatient-patient-pwa/types/global';

import { UseOrderFlowResult } from './useOrderFlow';
import useReasonSelection from './useReasonSelection';

export interface Props {
  admissionId?: UUIDv4;
  flow: UseOrderFlowResult;
}

/**
 * Renders the modal chain driven by the order trigger state machine
 * (select-child, chargeable-items, custom-fields, customization) plus the
 * coverage loading modal.
 */
const OrderFlowContainer: React.FunctionComponent<Props> = ({
  admissionId,
  flow,
}) => {
  const selectReason = useReasonSelection();
  const {
    currentStep,
    reason,
    serviceCustomFields,
    dispatch,
    shouldShowCustomFields,
  } = flow;

  return (
    <>
      {/* Modal for selecting children reasons */}
      {reason && currentStep === 'select-child' && (
        <PatientCallSelectorModal
          show
          reason={reason}
          onHide={() => dispatch({ type: 'reset', admissionId })}
          onSelect={(selection) => {
            const resolved = selectReason(selection);
            if (!resolved) return;
            dispatch({ type: 'select-child', reason: resolved });
          }}
        />
      )}

      {/* Modal for selecting order chargeable items */}
      {reason &&
        reason.chargeableItems &&
        currentStep === 'select-chargeable-items' && (
          <OrderChargeableItemsModal
            show={currentStep === 'select-chargeable-items'}
            reason={{ ...reason, chargeableItems: reason.chargeableItems }}
            onHide={() => dispatch({ type: 'reset', admissionId })}
            onComplete={(items) => {
              const orderId = uuid() as UUIDv4;
              const orderChargeableItems = Object.entries(items)
                .filter(([, count]) => count)
                .map(([id, count], i) => {
                  const choice = reason.chargeableItems?.find(
                    (item) => item.id === id,
                  ) as ChargeableItemFragment;
                  return {
                    orderId,
                    itemId: id as UUIDv4,
                    name: choice?.name,
                    description: choice?.description,
                    shortDescription: choice?.shortDescription,
                    sequentialOrder: i,
                    price: choice?.price,
                    quantity: count,
                  };
                });

              dispatch({
                type: 'select-chargeable-items',
                orderId,
                orderChargeableItems,
                customization: {
                  value: orderChargeableItems
                    .map((i) => `${i.quantity}x ${i.name}`)
                    .join(', '),
                },
              });
            }}
          />
        )}

      {/* Modal for selecting custom fields */}
      {reason && shouldShowCustomFields && (
        <PatientCallCustomFieldsModal
          show={shouldShowCustomFields}
          serviceCustomFields={serviceCustomFields}
          reasonName={reason?.reasonI18N}
          onHide={() => dispatch({ type: 'reset', admissionId })}
          onComplete={(values) => {
            // The alertId will be set correctly in the reducer
            // We use a placeholder here, the reducer will update it
            const placeholderAlertId = uuid() as UUIDv4;
            const formattedFields = formatOrderCustomFieldValues(
              placeholderAlertId,
              serviceCustomFields.map((field) => ({
                id: field.id,
                order: field.order,
                label: field.label,
                inputType: field.inputType,
                required: field.required,
                data: field.data,
              })),
              values,
            );
            dispatch({
              type: 'select-custom-fields',
              customFields: formattedFields,
            });
            // After dispatching custom fields, the reducer will move to 'trigger' step
          }}
        />
      )}

      {/* Modal for selecting alert customizations */}
      {reason && currentStep === 'select-customization' && (
        <PatientCallCustomizationModal
          show={currentStep === 'select-customization'}
          reason={reason}
          onHide={() => dispatch({ type: 'reset', admissionId })}
          onComplete={(customData) =>
            dispatch({
              type: 'select-customization',
              customization: customData,
            })
          }
        />
      )}

      {/* Display loader while checking coverage */}
      {currentStep === 'check-coverage' && (
        <Modal id="loading-modal" show closeButton={false}>
          <div className="d-flex flex-items-center">
            <Spinner size="sm" className="mr-4" />
            <Text size="md">Carregando atendimento...</Text>
          </div>
        </Modal>
      )}
    </>
  );
};

export default OrderFlowContainer;
